import { useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { toast } from 'sonner';

import { Autocomplete, useApiMutation } from '@/modules/common';

import { EventParticipantType } from '../types';

interface ParticipantUsageLeftInputProps {
  eventId: string;
  participantId: string;
  defaultValue: string;
  participantTypes: EventParticipantType[];
}

export function ParticipantTypeSelect({
  eventId,
  participantId,
  defaultValue,
  participantTypes,
}: ParticipantUsageLeftInputProps) {
  const queryClient = useQueryClient();
  const { control } = useFormContext();
  const [isUpdating, setIsUpdating] = useState(false);

  const updateParticipantMutation = useApiMutation<{ typeId: string }>(
    `/events/${eventId}/participants/${participantId}`,
    {
      method: 'PATCH',
    },
    {
      onError: (error) => {
        toast.error(error?.message);
      },
    }
  );

  const participantTypesOptions = useMemo(() => {
    return participantTypes.map((type) => ({
      id: type.id,
      value: type.value,
    }));
  }, [participantTypes]);

  const handleUpdateParticipant = async (value: string) => {
    if (value !== defaultValue) {
      setIsUpdating(true);

      try {
        await updateParticipantMutation.mutateAsync({
          typeId: value,
        });
        queryClient.invalidateQueries({ queryKey: ['eventParticipants'] });

        setIsUpdating(false);
      } finally {
        setIsUpdating(false);
      }
    }
  };

  return (
    <Autocomplete
      control={control}
      isLoading={isUpdating}
      name={`participants.${participantId}.type`}
      placeholder="Select type"
      options={participantTypesOptions}
      className="w-[200px] border-0"
      onChange={handleUpdateParticipant}
      defaultValue={defaultValue}
    />
  );
}
